import { useRef } from 'react';
import './modules/@shared/constants/i18n/config';
import './styles/base.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';
import { CssBaseline, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';

import { BuildInfo } from './modules/@shared/components';
import { ThemeProvider, useThemeContext } from './contexts/theme.context';
import router from './router';
import storefrontRouter from './modules/storefront/router';
import { getQueryClient } from './services/query-client';
import { GlobalStyle } from './styles/global-style';
import { ErrorBoundary } from './modules/@shared/components/ErrorBoundary';
import { manageScript } from './modules/@shared/helpers/manageScript';
import { GAInit } from './analytics';
import tracking from '~/services/tracking.ts';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { SessionContextProvider } from './contexts/session.context';
import { LaunchDarklySyncWrapper } from './components';

dayjs.extend(utc);
dayjs.extend(timezone);

GAInit();
tracking.init();

export function isStorefront() {
  return window.location.hostname === import.meta.env.VITE_STOREFRONT_DOMAIN;
}

function App() {
  if (!isStorefront()) {
    //@ts-ignore
    window.hsConversationsSettings = {
      loadImmediately: false,
      enableWidgetCookieBanner: false,
      disableAttachment: false,
    };

    manageScript({
      url: import.meta.env.VITE_HUBSPOT_SCRIPT_URL,
      id: import.meta.env.VITE_HUBSPOT_SCRIPT_ID,
    });
  }

  const queryClientRef = useRef(getQueryClient());
  const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

  return (
    <ErrorBoundary>
      <LDProvider clientSideID={clientSideID}>
        <LaunchDarklySyncWrapper>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <QueryClientProvider client={queryClientRef.current}>
              <SessionContextProvider>
                <ThemeProvider>
                  <CssBaseline />
                  <GlobalStyle />
                  <ThemeWrapper />
                </ThemeProvider>
                <Toaster richColors expand={true} position="bottom-center" />
                <BuildInfo />
                <ReactQueryDevtools />
              </SessionContextProvider>
            </QueryClientProvider>
          </LocalizationProvider>
        </LaunchDarklySyncWrapper>
      </LDProvider>
    </ErrorBoundary>
  );
}

function ThemeWrapper() {
  const { theme } = useThemeContext();

  return (
    <MuiThemeProvider theme={theme}>
      <RouterProvider router={isStorefront() ? storefrontRouter : router} />
    </MuiThemeProvider>
  );
}

export default App;
